import React, { useMemo } from 'react';

import { useAppNavigation } from '../../../hooks/useAppNavigation';
import { useMenus } from '../../../hooks/useMenus';
import { useMobile } from '../../../hooks/useMobile';
import { useTheme } from '../../../hooks/useTheme';

import {
  LAYOUT_GRID,
  LAYOUT_LIST,
  SORT_NAME_ASC,
  SORT_POPULAR,
  SORT_PRICE_ASC,
  SORT_PRICE_DESC
} from '../../../provider/MenuProvider';

import { ReactComponent as LayoutGridIcon } from '../../../icons/layout-grid.svg';
import { ReactComponent as LayoutListIcon } from '../../../icons/layout-list.svg';
import { ReactComponent as MealIcon } from '../../../icons/meal.svg';

import './menu.scss';

import { Categories } from '../../category/Categories';
import { Button } from '../../common/button/Button';
import Search from '../../common/search/Search';
import { Select } from '../../common/select/Select';
import { EmptyState } from '../../empty-state/EmptyState';
import Hero from '../../hero/Hero';
import { MenuGrid } from '../../menu/grid/MenuGrid';
import { MenuList } from '../../menu/list/MenuList';
import Navigation from '../../navigation/Navigation';
import { Promotions } from '../../promotion/Promotions';
import { Sidebar } from '../../sidebar/Sidebar';
import Tippy from '@tippyjs/react';

const Menu = () => {
  const isMobile = useMobile();
  const {
    promotions,
    categories,
    menus,
    query,
    menuLayout,
    setMenuLayout,
    menusLoading,
    sortBy,
    setSortBy
  } = useMenus();
  const { setShowMealChoiceModal } = useAppNavigation();
  const { getThemeVariable } = useTheme();
  const backgroundColor = useMemo(() => getThemeVariable('menu-button'), [getThemeVariable]);
  const textColor = useMemo(() => getThemeVariable('text-primary'), [getThemeVariable]);
  const menuLayoutEnabled = false;

  const sortOptions = [
    { id: SORT_POPULAR, label: 'Most popular', value: SORT_POPULAR },
    { id: SORT_PRICE_ASC, label: 'Price: Low to High', value: SORT_PRICE_ASC },
    { id: SORT_PRICE_DESC, label: 'Price: High to Low', value: SORT_PRICE_DESC },
    { id: SORT_NAME_ASC, label: 'Name: A-Z', value: SORT_NAME_ASC }
  ];

  const handleCreateMealClick = (e) => {
    e.stopPropagation();
    setShowMealChoiceModal(true);
  };

  const splitGrid = (item, size, index) => {
    if (!isMobile) {
      return (
        <MenuGrid
          key={`${item.title} ${index}`}
          title={item.title}
          items={item.menuItems}
          loading={menusLoading}
          showTitle={index === 0}
        />
      );
    }

    const splitItems = Array.from({ length: Math.ceil(item.menuItems.length / size) }, (_, i) =>
      item.menuItems.slice(i * size, i * size + size)
    );

    return splitItems.map((list, index) => (
      <MenuGrid
        key={`${item.title} ${index}`}
        title={item.title}
        items={list}
        loading={menusLoading}
        showTitle={index === 0}
      />
    ));
  };

  return (
    <>
      <Navigation>
        <div className="page-menu">
          <div>
            <div>
              {!isMobile && (
                <div className="page-menu__search">
                  {menuLayoutEnabled && menuLayout === LAYOUT_GRID && (
                    <Tippy content="List Layout" placement="bottom-start" disabled={isMobile}>
                      <LayoutListIcon
                        key={LAYOUT_GRID}
                        onClick={() => setMenuLayout(LAYOUT_LIST)}
                        style={{ background: backgroundColor, color: textColor }}
                      />
                    </Tippy>
                  )}
                  {menuLayoutEnabled && menuLayout === LAYOUT_LIST && (
                    <Tippy content="Grid Layout" placement="bottom-start" disabled={isMobile}>
                      <LayoutGridIcon
                        key={LAYOUT_LIST}
                        onClick={() => setMenuLayout(LAYOUT_GRID)}
                        style={{ background: backgroundColor, color: textColor }}
                      />
                    </Tippy>
                  )}
                  <Search />
                  <div className="page-menu__sort">
                    <Select
                      label="Sort"
                      showLabel
                      options={sortOptions}
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      dropdownWidth={200}
                    />
                  </div>
                  <Button className="page-menu__action-button" onClick={handleCreateMealClick}>
                    <MealIcon width={24} height={24} />
                    <span>BUILD YOUR MEAL</span>
                  </Button>
                </div>
              )}
              {!query && <Hero onClick={handleCreateMealClick} />}
              {!query && <Promotions promotions={promotions} />}
              {!query && <Categories categories={categories} />}
              {menus.length > 0 && (
                <div className="page-menu__menus">
                  {!menusLoading &&
                    menus.map((item, index) =>
                      menuLayout === LAYOUT_LIST ? (
                        <MenuList
                          key={`${item.title} ${index}`}
                          title={item.title}
                          items={item.menuItems}
                        />
                      ) : (
                        splitGrid(item, 10, index)
                      )
                    )}
                  {menuLayoutEnabled && menusLoading && (
                    <MenusShimmer rows={2} menuLayout={menuLayout} loading={menusLoading} />
                  )}
                </div>
              )}
              {!menusLoading && menus.length === 0 && (
                <div className="page-menu__empty">
                  <EmptyState
                    icon={() => <img src="/images/empty-menu.png" alt="empty menu" />}
                    title="No results found!"
                    description="We couldn't find any items matching your search.
                              Try exploring other categories or creating your own custom meal!"
                  />
                </div>
              )}
            </div>
          </div>
          {!isMobile && <Sidebar />}
          {isMobile && (
            <Button
              className="page-menu__action-button page-menu__action-button--fab"
              onClick={handleCreateMealClick}
            >
              <MealIcon width={24} height={24} />
              <span>BUILD YOUR MEAL</span>
            </Button>
          )}
        </div>
      </Navigation>
    </>
  );
};

const MenusShimmer = ({ rows, menuLayout, loading }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
    {Array.from(Array(rows).keys()).map((key) =>
      menuLayout === LAYOUT_LIST ? (
        <MenuList key={key} loading={loading} />
      ) : (
        <MenuGrid key={key} loading={loading} />
      )
    )}
  </div>
);

export default Menu;
